import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { ShoppingBag, CheckCircle, Clock, Trash2, User, Coins, CreditCard, ReceiptText, Calendar, Image as ImageIcon, ExternalLink, Phone, MapPin, Truck, Store } from 'lucide-react';

const OrdersAdmin = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchOrders(); }, []);

  const fetchOrders = async () => {
    try {
      const res = await axios.get('https://my-pharmacy-api.onrender.com/api/orders');
      setOrders(res.data);
    } catch (err) {
      console.error("Error fetching orders:", err);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, currentStatus) => {
    if (currentStatus === 'تم الدفع') return;
    
    try {
      await axios.put(`https://my-pharmacy-api.onrender.com/api/orders/${id}`, { status: 'تم الدفع' });
      fetchOrders();
    } catch (err) {
      alert("فشل في تحديث حالة الطلب");
    }
  };

  const deleteOrder = async (id) => {
    if (window.confirm("هل تريد حذف هذا الطلب نهائياً؟")) {
      try {
        await axios.delete(`https://my-pharmacy-api.onrender.com/api/orders/${id}`);
        // التأكد من الحذف باستخدام id أو _id
        setOrders(prevOrders => prevOrders.filter(o => (o.id || o._id) !== id));
        alert("✅ تم الحذف بنجاح");
      } catch (err) {
        alert("❌ فشل الحذف: تأكد من اتصال السيرفر");
      }
    }
  };

  if (loading) return (
    <div className="flex justify-center items-center h-64">
      <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-blue-600"></div>
    </div>
  );

  return (
    <div dir="rtl" className="max-w-6xl mx-auto space-y-8 pb-10 px-4 text-right">
      {/* الرأس - الهيدر */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-8 rounded-[35px] shadow-sm border border-gray-50">
        <div>
          <h2 className="text-3xl font-black text-gray-800 tracking-tight">إدارة مبيعات شام كاش</h2>
          <p className="text-gray-400 font-bold mt-1">راجع بيانات الزبائن، طرق التوصيل، وتأكيد الحوالات</p>
        </div>
        <div className="flex items-center gap-3 bg-blue-50 px-6 py-3 rounded-2xl">
          <ShoppingBag className="text-blue-600" />
          <span className="text-blue-700 font-black text-lg">{orders.length} طلب وارد</span>
        </div>
      </div>

      {/* قائمة الطلبات */}
      <div className="grid gap-8">
        {orders.map(order => (
          <div key={order.id || order._id} className="bg-white rounded-[45px] shadow-sm border border-gray-100 overflow-hidden transition-all hover:shadow-xl group">
            <div className="p-8">
              <div className="flex flex-col lg:flex-row gap-8">
                
                {/* 1. صورة الإيصال المرفوعة من Cloudinary */}
                <div className="lg:w-1/4">
                  <p className="text-[10px] font-black text-gray-400 uppercase mb-3 mr-2 text-right">إيصال الدفع المرفق</p>
                  {order.receipt_url ? (
                    <div className="relative group/img overflow-hidden rounded-[30px] border-2 border-gray-50 aspect-square bg-gray-50">
                      <img 
                        src={order.receipt_url} // تعديل: الرابط مباشر من Cloudinary
                        className="w-full h-full object-cover transition-transform duration-500 group-hover/img:scale-110"
                        alt="Receipt"
                      />
                      <a 
                        href={order.receipt_url} // تعديل: الرابط مباشر لفتحه في نافذة جديدة
                        target="_blank" 
                        rel="noreferrer"
                        className="absolute inset-0 bg-black/40 opacity-0 group-hover/img:opacity-100 transition-opacity flex items-center justify-center text-white gap-2 font-bold text-sm"
                      >
                        <ExternalLink size={18} /> عرض الإيصال كاملاً
                      </a>
                    </div>
                  ) : (
                    <div className="w-full aspect-square rounded-[30px] bg-gray-50 flex flex-col items-center justify-center text-gray-300 border-2 border-dashed border-gray-100">
                      <ImageIcon size={40} strokeWidth={1} />
                      <span className="text-xs font-bold mt-2">لا يوجد إيصال</span>
                    </div>
                  )}
                </div>

                {/* 2. بيانات الزبون والحالة */}
                <div className="flex-1 space-y-6">
                  <div className="flex justify-between items-start">
                    <div className="flex items-center gap-4 text-right">
                      <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${order.payment_status === 'تم الدفع' ? 'bg-green-50 text-green-500' : 'bg-orange-50 text-orange-500'}`}>
                        <User size={28} />
                      </div>
                      <div>
                        <h3 className="font-black text-2xl text-gray-800">{order.customer_name}</h3>
                        <div className="flex items-center gap-3 text-gray-400 mt-1 font-bold text-[11px] uppercase tracking-tighter">
                          <span className="flex items-center gap-1"><Phone size={14} className="text-green-500"/> {order.customer_phone || order.phone || 'بدون رقم'}</span>
                          <span className="flex items-center gap-1"><Calendar size={14}/> {new Date(order.created_at).toLocaleDateString('ar-SY')}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="text-left">
                      <div className="bg-gray-50 px-5 py-2 rounded-2xl border border-gray-100 inline-block text-left">
                         <span className="block text-[9px] font-black text-gray-400 uppercase mb-1 tracking-widest text-center">إجمالي المبلغ</span>
                         <div className="flex items-center gap-2">
                           <Coins className="text-yellow-500" size={16} />
                           <span className="text-xl font-black text-gray-800">{Number(order.total_price_syp).toLocaleString()}</span>
                           <span className="text-xs font-bold text-gray-500">ل.س</span>
                         </div>
                      </div>
                    </div>
                  </div>

                  {/* 3. تنبيه طريقة الاستلام/التوصيل */}
                  <div className={`flex items-center gap-4 p-4 rounded-3xl border ${order.delivery_type === 'delivery' ? 'bg-purple-50 border-purple-100' : 'bg-blue-50 border-blue-100'}`}>
                    <div className={`p-3 rounded-2xl ${order.delivery_type === 'delivery' ? 'bg-purple-500 text-white' : 'bg-blue-500 text-white'}`}>
                      {order.delivery_type === 'delivery' ? <Truck size={22}/> : <Store size={22}/>}
                    </div>
                    <div className="flex-1">
                      <span className={`block text-[10px] font-black uppercase mb-0.5 ${order.delivery_type === 'delivery' ? 'text-purple-400' : 'text-blue-400'}`}>
                        طريقة الاستلام
                      </span>
                      <h4 className={`text-lg font-black ${order.delivery_type === 'delivery' ? 'text-purple-900' : 'text-blue-900'}`}>
                        {order.delivery_type === 'delivery' ? 'توصيل منزلي للعنوان المذكور' : 'استلام شخصي من الصيدلية'}
                      </h4>
                      {order.delivery_type === 'delivery' && (
                        <div className="flex items-center gap-2 mt-1 text-purple-700 font-bold text-sm">
                          <MapPin size={16} />
                          {order.address}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* تفاصيل الأصناف */}
                  <div className="p-5 bg-gray-50 rounded-[25px] border border-gray-100 flex items-start gap-4 text-right">
                    <div className="p-2 bg-white rounded-xl text-gray-400 shadow-sm mt-0.5">
                      <ReceiptText size={18} />
                    </div>
                    <div>
                      <span className="block text-[10px] font-black text-gray-400 uppercase mb-1">الأصناف المطلوبة</span>
                      <p className="text-sm font-bold text-gray-700 leading-relaxed">
                        {order.order_details}
                      </p>
                    </div>
                  </div>

                  {/* أزرار الإجراءات */}
                  <div className="flex items-center gap-3 pt-2">
                    <button 
                      onClick={() => updateStatus(order.id || order._id, order.payment_status)}
                      className={`flex-1 py-4 rounded-[22px] font-black text-sm flex items-center justify-center gap-3 transition-all ${
                        order.payment_status === 'تم الدفع' 
                        ? 'bg-green-500 text-white shadow-lg shadow-green-100 cursor-default' 
                        : 'bg-gray-900 text-white hover:bg-green-600 shadow-xl shadow-gray-200 active:scale-95'
                      }`}
                    >
                      {order.payment_status === 'تم الدفع' ? (
                        <><CheckCircle size={20} /> تم التأكيد واستلام المبلغ</>
                      ) : (
                        <><CreditCard size={20} /> تأكيد مطابقة الإيصال</>
                      )}
                    </button>
                    
                    <button 
                      onClick={() => deleteOrder(order.id || order._id)}
                      className="p-4 bg-red-50 text-red-300 rounded-[22px] hover:bg-red-500 hover:text-white transition-all active:scale-90 shadow-sm shadow-red-50"
                    >
                      <Trash2 size={22} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}

        {orders.length === 0 && (
          <div className="text-center py-24 bg-white rounded-[50px] border-2 border-dashed border-gray-100">
            <div className="bg-gray-50 w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6">
              <ShoppingBag size={48} className="text-gray-200" />
            </div>
            <h3 className="text-xl font-black text-gray-400">لا توجد طلبات واردة حالياً</h3>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrdersAdmin;